/**
 * Text typed or pasted on phones can carry invisible format characters (word
 * joiner U+2060, zero-width spaces, byte order mark, soft hyphen) and unusual
 * spaces (no-break, narrow, em). The PDF font has no glyph for the former, so
 * pdfmake draws its missing-glyph box for them, and its line breaker can then
 * swallow the following space: a customer's "- " + U+2060 + "seinän
 * paikkauksien" came out as "- [box]seinänpaikkauksien" (2026-10-20). The
 * document definition is cleaned right before pdfmake gets it; the stored
 * text is left as it is.
 *
 * The character classes are built from code points so that no invisible
 * character has to live in this source file.
 */

const chars = (...codePoints) =>
  codePoints.map((codePoint) => String.fromCodePoint(codePoint)).join('');
const range = (from, to) =>
  `${String.fromCodePoint(from)}-${String.fromCodePoint(to)}`;

// Soft hyphen, zero-width space/non-joiner/joiner and direction marks
// (U+200B-U+200F), word joiner and invisible operators (U+2060-U+2064),
// deprecated format characters (U+206A-U+206F), byte order mark (U+FEFF).
const INVISIBLE_FORMAT_CHARS = new RegExp(
  `[${chars(0x00ad)}${range(0x200b, 0x200f)}${range(0x2060, 0x2064)}${range(0x206a, 0x206f)}${chars(0xfeff)}]`,
  'g'
);
// No-break space, ogham space mark, en/em/thin/hair spaces (U+2000-U+200A),
// narrow no-break space, medium mathematical space, ideographic space.
const UNUSUAL_SPACES = new RegExp(
  `[${chars(0x00a0, 0x1680)}${range(0x2000, 0x200a)}${chars(0x202f, 0x205f, 0x3000)}]`,
  'g'
);
// Line separator and paragraph separator.
const UNUSUAL_LINE_BREAKS = new RegExp(`[${chars(0x2028, 0x2029)}]`, 'g');

export const sanitizePdfText = (text) =>
  text
    .replace(INVISIBLE_FORMAT_CHARS, '')
    .replace(UNUSUAL_SPACES, ' ')
    .replace(UNUSUAL_LINE_BREAKS, '\n');

// Image data is long and never contains these characters: skipped.
const SKIPPED_KEYS = new Set(['image', 'svg', 'qr']);

// A data object (as opposed to a Date, a buffer or a class instance). Checked
// by the prototype's constructor name, so objects from another realm (an
// iframe, a test VM) count too.
const isPlainObject = (value) => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return (
    proto === null || (proto.constructor && proto.constructor.name === 'Object')
  );
};

/**
 * Returns a copy of a pdfmake document definition with every text string
 * cleaned. Arrays and plain objects are walked; header, footer and background
 * functions are wrapped so what they return is cleaned too; anything else
 * (buffers, dates) is passed through untouched.
 */
export const sanitizePdfDocDefinition = (value, key) => {
  if (typeof value === 'string') {
    if (SKIPPED_KEYS.has(key) || value.startsWith('data:')) {
      return value;
    }
    return sanitizePdfText(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizePdfDocDefinition(item));
  }

  if (isPlainObject(value)) {
    const cleaned = {};
    for (const [childKey, childValue] of Object.entries(value)) {
      cleaned[childKey] = sanitizePdfDocDefinition(childValue, childKey);
    }
    return cleaned;
  }

  if (typeof value === 'function') {
    return (...args) => sanitizePdfDocDefinition(value(...args));
  }

  return value;
};
